import { UserRole } from "@/__generated__/graphql";
import { useUserContext } from "@/contexts/UserContextProvider";
import { DEFAULT_CURRENCY } from "@/helpers/constants";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { useState, useEffect } from "react";

const moneyFieldRegExp = /(-?[0-9.]+) (\w+)/;

export const useFormatCurrency = () => {
  const { currentUser } = useUserContext();
  return (amount?: string | number | null) => {
    if (currentUser?.role === UserRole.Reviewer) {
      return "-----";
    }
    let number: number;
    let currency = DEFAULT_CURRENCY;
    if (typeof amount === "number" || amount === null || amount === undefined) {
      number = amount || 0;
    } else {
      const result = moneyFieldRegExp.exec(amount);
      if (result && result.length >= 3) {
        number = Number(result[1]);
        currency = result[2];
      } else {
        number = Number(amount);
      }
    }

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    });

    return formatter.format(number);
  };
};

export const useDebouncedSearchParam = (key: string, delay = 1000) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialValue = searchParams.get(key) || "";
  const [value, setValue] = useState(initialValue);
  const [debouncedValue] = useDebounce(value, delay);

  useEffect(() => {
    setSearchParams((prev) => {
      if (debouncedValue) {
        prev.set(key, debouncedValue);
      } else {
        prev.delete(key);
      }
      return prev;
    });
  }, [debouncedValue, key, setSearchParams]);

  return [value, setValue] as const;
};

export const usePageTitle = (title: string) => {
  useEffect(() => {
    document.title = `${title} - Hotspring`;
  }, [title]);
};

export const useCountdown = (date: Date, interval = 30 * 1000) => {
  // returns something like "~2hr 10min"
  const [label, setLabel] = useState("");
  const fallbackLabel = "(Finishing up soon..)";

  useEffect(() => {
    function update() {
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      if (diffMs <= 0) {
        setLabel(fallbackLabel);
        clearInterval(timer);
        return;
      }
      const totalMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(totalMins / 60);
      const mins = totalMins % 60;
      const parts = [];
      if (hours) parts.push(`${hours}hr`);
      if (mins) parts.push(`${mins}min`);

      if (!parts.length) setLabel(fallbackLabel);
      else setLabel(`(~${parts.join(" ")})`);
    }

    const timer = setInterval(update, interval);
    update();
    return () => clearInterval(timer);
  }, [date, interval]);

  return label;
};
